import { PlatformLocation } from '@angular/common';
import { Injectable } from '@angular/core';
import { KnownRequestInstrumentationEvents } from '@office-start/telemetry-types';
import { of as observableOf } from 'rxjs';

import { OfficeHomePageErrorHandler } from '../error-handler/error-handler.service';
import { ErrorId } from '../error-handler/error-id';
import { HttpStatusCode } from '../shared/constants/http-status-code.enum';
import { OfficeRoute } from '../shared/constants/office-route.enum';
import { InstrumentationParam, InstrumentationService, KnownInstrumentationEvents } from '../shared/instrumentation/instrumentation.service';
import { FetchStrategy, InstrumentedHttp, InstrumentedHttpPromise } from '../shared/instrumentation/instrumented-http.service';
import { Cacheable } from '../shared/models/cacheable.model';
import { HttpRequestOptions } from '../shared/models/http-request-options.model';
import { OperationStream } from '../shared/models/operation-stream.model';
import { AccessTokenConsumer, AccessTokenService } from '../shared/services/access-token.service';
import { OfficeRouteService } from '../shared/services/office-route.service';
import { OfficeHomeApiConfigService } from '../shared/services/officehome-api-config.service';
import { SchedulerService } from '../shared/services/scheduler.service';
import { UpdateContentService } from '../shared/services/update-content.service';
import { WindowService } from '../shared/services/window.service';
import { XhrService, XhrServiceError } from '../shared/services/xhr.service';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { ClientStartupConfigService } from '../shared/shared-context/client-startup-config.service';
import { ContextService } from '../shared/shared-context/context.service';
import { GuidGenerator } from '../shared/utilities/guid-generator.utility';
import { ObservableHelper } from '../shared/utilities/observable-helper.utility';
import { SiteItem } from './models/site-item.model';
import { SharePointConfigService } from './sharepoint-config.service';

export interface SharePointRawData { // All returned SharePoint data
    Title: string;
    User: SharePointUser;
    TenantThumbnails: Array<SharePointThumbnail>;
    DefaultSitesThumbnails: Array<SharePointThumbnail>;
    SitesThumbnailBackgroundColor: string;
    ExpiresInMinutes: number;
    Sites: Array<SharePointSiteWrapper>;
    Error?: SharePointSitesError;
    Analytics: SharePointAnalytics;
}

export interface SharePointData extends Cacheable { // Returned SharePoint data with Frequent/Following parsed out
    Title: string;
    User: SharePointUser;
    TenantThumbnails: Array<SharePointThumbnail>;
    DefaultSitesThumbnails: Array<SharePointThumbnail>;
    SitesThumbnailBackgroundColor: string;
    ExpiresInMinutes: number;
    FrequentSites: Array<SharePointItem>;
    FollowingSites: Array<SharePointItem>;
    Analytics: SharePointAnalytics;
}

export interface SharePointSiteWrapper {
    Title: string;
    Sites: Array<SharePointItem>;
}

export interface SharePointAnalytics {
    Duration: number;
    StatusCode: number;
    CorrelationId: string;
    CreationTime: string;
    Activities: Array<SharePointActivity>;
}

export interface SharePointActivity {
    Name: string;
    Duration: number;
    StatusCode: number;
    Message: string;
}

export interface SharePointThumbnail {
    Size: number;
    Location: string;
}

export interface SharePointUser {
    ObjectId: string;
    Upn: string;
    Puid: string;
    TenantId: string;
}

export interface SharePointItem { // A single SharePoint Site
    ItemReference: SharePointItemReference;
    Acronym: string;
    BannerImageUrl: string;
    BannerColor: string;
    WebTemplate: string;
    Time: string;
    Url: string;
    OriginalUrl: string;
    Title: string;
    Weight: number;
    Type: string;
}

export interface SharePointItemReference {
    SiteId: string;
    WebId: string;
    IndexId: string;
    Type: string;
}

export interface SharePointSitesError {
    CanRetry: boolean;
    Code: number;
    Name: string;
    Message: string;
    LocalizedMessage: string;
}

const enum SharepointSitesIndex {
    Frequent = 0,
    Following = 1
}

@Injectable({
    providedIn: 'root'
})
export class SharePointSitesService extends XhrService {
    private static readonly forceRefreshParam = '&forcerefresh=1';
    private static readonly sharepointPath = 'sites';

    private readonly sitesUrl: string;

    // Includes data from cache, as well as latest updates retrieved via network call
    private sharepointDataStream: OperationStream<SharePointData>;
    private requestIndex = 0;

    constructor(
        accessTokenService: AccessTokenService,
        private readonly clientStartupConfigService: ClientStartupConfigService,
        contextService: ContextService,
        instrumentationService: InstrumentationService,
        instrumentedHttp: InstrumentedHttp,
        officeHomeApiConfigService: OfficeHomeApiConfigService,
        officeRouteService: OfficeRouteService,
        platformLocation: PlatformLocation,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler,
        sharePointConfigService: SharePointConfigService,
        private readonly updateContentService: UpdateContentService,
        scheduler: SchedulerService,
        private readonly windowService: WindowService,
        private readonly capabilitiesService: CapabilitiesService
    ) {
        super(accessTokenService, instrumentationService, instrumentedHttp, AccessTokenConsumer.MruPlusPlus, contextService, officeHomeApiConfigService, platformLocation, scheduler);

        const config = sharePointConfigService.config;
        if ((!this.inMockMode && !(config && config.sharePointServiceRoot)) || !this.capabilitiesService.capabilities.sharePointEnabled) {
            return;
        }

        const sharedContext = contextService.sharedContext;
        const lcid = sharedContext.officeMarketLcid.toString();
        this.sitesUrl = this.inMockMode
            ? `${sharedContext.officeHomeApiRoot}/${SharePointSitesService.sharepointPath}`
            : `${config.sharePointServiceRoot}/${SharePointSitesService.sharepointPath}?lcid=${lcid}&uilcid=${lcid}&syslcid=${lcid}&lists=frequent,followed&items=${config.maxTotalSharePointSites}`;

        // ICE caches the data for 30 min
        if (!this.inMockMode && config.forceSharePointRefresh) {
            this.sitesUrl += SharePointSitesService.forceRefreshParam;
        }

        // Start fetch of SharePoint sites without waiting for component to render
        // TODO: Should we preload suggestions for FAST search box?
        if (!this.capabilitiesService.capabilities.edgeworthEnabled) {
            // Fetch SPO Sites early only if we're not showing Edgeworth experience
            officeRouteService.subscribeToRouteChangeStart(event => {
                if (event.currentRoute && (event.currentRoute.route === OfficeRoute.OfficeHome || event.currentRoute.route === OfficeRoute.Pwa)) {
                    this.getSharePointSites();
                }
            });
        }
    }

    /**
     * Retrieves SharePoint data
     */
    public getSharePointSites(): OperationStream<SharePointData> {
        if (this.sharepointDataStream) {
            return this.sharepointDataStream;
        }

        if (this.sitesUrl) {
            const sitesFromCache = this.sitesFetch(FetchStrategy.CacheOnly);
            const sitesFromNetwork = this.capabilitiesService.capabilities.prefetchDataEnabled ?
            this.prefetchSitesData() : this.sitesFetch(FetchStrategy.NetworkFirst);
            const updateStream = this.updateContentService.getOrAddUpdateStream('sharepointsites', () => this.sitesFetch(FetchStrategy.NetworkFirst));
            this.sharepointDataStream = ObservableHelper.createResultsWithCacheObservable(
                sitesFromCache,
                sitesFromNetwork,
                updateStream,
                _ => this.sharepointDataStream = null // clear the cached stream on failure so retry is possible
            );
        } else {
            this.sharepointDataStream = { results: observableOf(null), initialCompletion: Promise.resolve(null) };
        }

        return this.sharepointDataStream;
    }

    protected isRetriableError(status: number) {
        return status === HttpStatusCode.Throttled;
    }

    protected handleQueryParam(paramNameInLowerCase: string, value: string | Array<string>, requestConfig: HttpRequestOptions) {
        if (paramNameInLowerCase === 'mocksharepoint' && this.mockRequestHeader) {
            requestConfig.headers.set(this.mockRequestHeader, value);
        }
    }

    private sitesFetch(strategy: FetchStrategy): Promise<SharePointData> {
        return new Promise<SharePointData>((resolve, reject) => {
            const promiseCallback = (promise: InstrumentedHttpPromise<SharePointRawData>) => {
                promise
                    .instrument((result, params) => {
                        params[InstrumentationParam.RequestId] = result.headers && result.headers.get('X-CorrelationId');
                        params[InstrumentationParam.PartnerGeo] = result.headers && result.headers.get('X-OfficeCluster');
                        params[InstrumentationParam.Index] = (this.requestIndex++).toString();
                        // TODO: Remove once partial failure rate investigation is over (ETA: 5/1/20)
                        params[InstrumentationParam.SharePointSiteLists] = (result.ok && result.data) ?
                            ((result.data.Sites && result.data.Sites.length) ? result.data.Sites.map( list => list ? `${list.Title || 'NULLTITLE'}:${list.Sites ? 'SOME' : 'NULLSITES'}` : 'NULLLIST').join(',') : 'NONE') : 'ERROR';

                        if (result.ok && result.data && result.data.Sites && result.data.Sites.length === 2 && result.data.Sites[0] && result.data.Sites[1]) {
                            const frequentSites = result.data.Sites[SharepointSitesIndex.Frequent].Sites;
                            const followingSites = result.data.Sites[SharepointSitesIndex.Following].Sites;
                            if (frequentSites && followingSites) {
                                const itemsCount = (frequentSites ? frequentSites.length : 0) + (followingSites ? followingSites.length : 0);
                                const both = frequentSites.filter(freqSite => followingSites.filter(followSite => followSite.ItemReference.SiteId === freqSite.ItemReference.SiteId).length > 0);
                                const types = result.data.Sites.map(wrapper => wrapper.Sites.map(item => item.ItemReference.Type)).reduce((x, xs) => x.concat(xs), []);

                                params[InstrumentationParam.ActualSize] = itemsCount.toString();
                                params[InstrumentationParam.SharePointFrequentSitesCount] = frequentSites.length.toString();
                                params[InstrumentationParam.SharePointFollowingSitesCount] = followingSites.length.toString();
                                params[InstrumentationParam.SharePointFollowingAndFrequentCount] = both.length.toString();
                                params[InstrumentationParam.Distribution] = SiteItem.distributionFromSiteTypes(types);
                            }
                        } else if (result.data && result.data.Error) {
                            params[InstrumentationParam.Error] = JSON.stringify(result.data.Error); // Sites API claims to not send PII in error responses
                        }
                    })
                    .then(response => {
                        if (response.data) {
                            resolve(this.convertRawData(response.data, response.fromCache));
                        } else {
                            reject(new XhrServiceError(response, 'No data returned'));
                        }
                    })
                    .catch(error => {
                        this.globalErrorHandler.handleError(error, ErrorId.ZvxNn);
                    });
            };

            this.get<SharePointRawData>(KnownInstrumentationEvents.SharePointSitesRequest, KnownRequestInstrumentationEvents.SharePointSitesRequest, this.sitesUrl, promiseCallback, reject, this.getNewRequestConfig(), strategy);
        });
    }

    private prefetchSitesData(): Promise<SharePointData> {
        if (this.windowService.nativeWindow.prefetchSharepointSitesDataPromise) {
            return new Promise<SharePointData>((resolve, reject) => {
                this.windowService.nativeWindow.prefetchSharepointSitesDataPromise.then(sitesResponse => {
                    if (sitesResponse) {
                        resolve(this.convertRawData(sitesResponse, false));
                    } else {
                        reject(new XhrServiceError(sitesResponse, 'No data returned'));
                    }
                })
                .catch (error => {
                    this.globalErrorHandler.handleError(error, ErrorId.MThMj);
                    reject(error);
                });
            });
        } else {
            return this.sitesFetch(FetchStrategy.NetworkFirst);
        }
    }

    private convertRawData(rawData: SharePointRawData, fromCache = false): SharePointData {
        let frequentSites = [];
        let followingSites = [];
        if (rawData && rawData.Sites && rawData.Sites[SharepointSitesIndex.Frequent] && rawData.Sites[SharepointSitesIndex.Following]) {
            frequentSites = rawData.Sites[SharepointSitesIndex.Frequent].Sites;
            followingSites = rawData.Sites[SharepointSitesIndex.Following].Sites;
        }
        return {
            Title: rawData.Title,
            User: rawData.User,
            TenantThumbnails: rawData.TenantThumbnails,
            DefaultSitesThumbnails: rawData.DefaultSitesThumbnails,
            SitesThumbnailBackgroundColor: rawData.SitesThumbnailBackgroundColor,
            ExpiresInMinutes: rawData.ExpiresInMinutes,
            FrequentSites: frequentSites,
            FollowingSites: followingSites,
            Analytics: rawData.Analytics,
            fromCache
        };
    }

    private getNewRequestConfig(): HttpRequestOptions {
        return { headers: this.requestHeaders(GuidGenerator.createGuid()) };
    }

    private requestHeaders(requestId: string): Map<string, string | Array<string>> {
        return new Map([
            ['Accept', 'application/json'],
            ['X-CorrelationId', requestId],
            ['X-Office-Platform', 'Web'], // Note, use S2S if calls move to server
            ['X-Office-Application', this.contextService.sharedContext.iceApplicationId.toString()],
            ['X-Office-Version', this.clientStartupConfigService.config.sharedClientStartupContext.buildId]
        ]);
    }
}
